import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiActivity, FiAlertTriangle, FiDollarSign, FiFileText, FiLifeBuoy, FiPieChart, FiSettings, FiUsers } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import styles from './AdminDashboardPage.module.css';
import { AdminService } from '../services/admin.service';
import { UsersService } from '../services/users.service';
import { DeveloperRequestsService } from '../services/developer-requests.service';
import { TransferProofService } from '../services/transfer-proof.service';
import { AdminInvestmentsService } from '../services/adminInvestments.service';
import { AdminWithdrawalsService } from '../services/admin-withdrawals.service';
import { AdminDevelopmentsService } from '../services/adminDevelopments.service';
import { SupportService } from '../services/support.service';
import { AuditService } from '../services/audit.service';

type Trend = 'positive' | 'negative' | 'neutral';

interface Stat {
  id: number;
  label: string;
  value: string;
  change: string;
  trend: Trend;
  icon: React.ElementType;
}

type DashboardData = {
  stats: Awaited<ReturnType<typeof AdminService.getStats>> | null;
  settings: Awaited<ReturnType<typeof AdminService.getSystemSettings>> | null;
  pendingUsers: Awaited<ReturnType<typeof UsersService.getPending>>;
  pendingDevReqs: Awaited<ReturnType<typeof DeveloperRequestsService.getAll>> | null;
  transferProofsPendingTotal: number;
  investmentsProjects: Awaited<ReturnType<typeof AdminInvestmentsService.getProjects>> | null;
  withdrawals: Awaited<ReturnType<typeof AdminWithdrawalsService.list>> | null;
  developments: Awaited<ReturnType<typeof AdminDevelopmentsService.list>> | null;
  supportStats: Awaited<ReturnType<typeof SupportService.getStats>> | null;
  auditFailed24h: Awaited<ReturnType<typeof AuditService.getEvents>> | null;
};

type FlagItem = {
  key: string;
  value: boolean;
};

function ymd(d: Date) {
  return d.toISOString().slice(0, 10);
}

export default function AdminDashboardPage() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  const isSpanish = i18n.language.startsWith('es');
  const locale = isSpanish ? 'es-MX' : 'en-US';
  const currency = isSpanish ? 'MXN' : 'USD';

  const formatCurrency = (amount: number) =>
    Number(amount || 0).toLocaleString(locale, { style: 'currency', currency, minimumFractionDigits: 0 });

  useEffect(() => {
    let alive = true;

    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);

    setLoading(true);
    Promise.allSettled([
      AdminService.getStats(),
      AdminService.getSystemSettings(),
      UsersService.getPending(),
      DeveloperRequestsService.getAll({ status: 'PENDING' }),
      TransferProofService.getAll({ status: 'PENDING' }),
      AdminInvestmentsService.getProjects(),
      AdminWithdrawalsService.list(),
      AdminDevelopmentsService.list({ page: 1, pageSize: 100 }),
      SupportService.getStats(),
      AuditService.getEvents({ result: 'FAIL', from: ymd(yesterday), to: ymd(now), page: 1, pageSize: 20 }),
    ])
      .then((results) => {
        if (!alive) return;
        const [statsR, settingsR, usersR, devReqR, proofsR, invProjectsR, withdrawalsR, devsR, supportR, auditR] = results;

        setData({
          stats: statsR.status === 'fulfilled' ? statsR.value : null,
          settings: settingsR.status === 'fulfilled' ? settingsR.value : null,
          pendingUsers: usersR.status === 'fulfilled' && Array.isArray(usersR.value) ? usersR.value : [],
          pendingDevReqs: devReqR.status === 'fulfilled' ? devReqR.value : null,
          transferProofsPendingTotal: proofsR.status === 'fulfilled' ? Number(proofsR.value?.total || 0) : 0,
          investmentsProjects: invProjectsR.status === 'fulfilled' ? invProjectsR.value : null,
          withdrawals: withdrawalsR.status === 'fulfilled' ? withdrawalsR.value : null,
          developments: devsR.status === 'fulfilled' ? devsR.value : null,
          supportStats: supportR.status === 'fulfilled' ? supportR.value : null,
          auditFailed24h: auditR.status === 'fulfilled' ? auditR.value : null,
        });
      })
      .finally(() => {
        if (!alive) return;
        setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [currency, i18n.language, locale]);

  const stats: Stat[] = useMemo(() => {
    const totalCapital = data?.stats?.totalCapital ?? 0;
    const activeProjects = data?.stats?.activeProjects ?? 0;
    const totalUsers = data?.stats?.totalUsers ?? 0;
    const conversionRate = data?.stats?.conversionRate ?? 0;

    return [
      {
        id: 1,
        label: t('admin.stats.total_capital'),
        value: formatCurrency(totalCapital),
        change: data?.stats?.totalCapitalChange ?? '',
        trend: 'neutral',
        icon: FiDollarSign
      },
      {
        id: 2,
        label: t('admin.stats.active_projects'),
        value: String(activeProjects),
        change: data?.stats?.activeProjectsChange ?? '',
        trend: 'neutral',
        icon: FiActivity
      },
      {
        id: 3,
        label: t('admin.stats.total_users'),
        value: String(totalUsers),
        change: data?.stats?.totalUsersChange ?? '',
        trend: (data?.stats?.totalUsersTrend ?? 'neutral') as Trend,
        icon: FiUsers
      },
      {
        id: 4,
        label: t('admin.stats.conversion_rate'),
        value: `${conversionRate}%`,
        change: data?.stats?.conversionRateChange ?? '',
        trend: 'neutral',
        icon: FiPieChart
      }
    ];
  }, [data?.stats, t]);

  const financialSummary = useMemo(() => {
    const projects = Array.isArray(data?.investmentsProjects) ? data!.investmentsProjects : [];
    const withdrawals = Array.isArray(data?.withdrawals) ? data!.withdrawals : [];

    const activeProjects = projects.filter((p) => Number(p.closuresCount || 0) === 0).length;
    const closedProjects = projects.filter((p) => Number(p.closuresCount || 0) > 0).length;
    const totalInvested = projects.reduce((sum, p) => sum + Number(p.totalInvested || 0), 0);

    const withdrawalsByStatus = withdrawals.reduce(
      (acc, w) => {
        const status = String(w.status || 'PENDING');
        acc[status] = (acc[status] || 0) + 1;
        acc.pendingAmount += status === 'PENDING' ? Number(w.amount || 0) : 0;
        return acc;
      },
      { PENDING: 0, APPROVED: 0, EXECUTED: 0, REJECTED: 0, pendingAmount: 0 } as Record<string, number>,
    );

    return {
      totalInvested,
      activeProjects,
      closedProjects,
      withdrawalsByStatus,
    };
  }, [data?.investmentsProjects, data?.withdrawals]);

  const queueSummary = useMemo(() => {
    const pendingUsers = data?.pendingUsers?.length || 0;
    const pendingDevReqs = Number(data?.pendingDevReqs?.total || 0);
    const transferProofs = Number(data?.transferProofsPendingTotal || 0);
    const devItems =
      data?.developments && (data.developments as any).ok !== false ? ((data.developments as any).data?.items ?? []) : [];

    const documentationReview = devItems.filter((d: any) =>
      ['IN_REVIEW', 'OBSERVED', 'UPLOADED'].includes(String(d.documentation_status || 'NONE')),
    ).length;

    return { pendingUsers, pendingDevReqs, transferProofs, documentationReview };
  }, [data?.pendingUsers, data?.pendingDevReqs, data?.transferProofsPendingTotal, data?.developments]);

  const riskSummary = useMemo(() => {
    const auditEvents = data?.auditFailed24h?.data || [];
    const failCount = auditEvents.length;
    const mfaFail = auditEvents.filter((e) => String(e.event_type || '').includes('MFA')).length;
    const loginFail = auditEvents.filter((e) => String(e.event_type || '').includes('LOGIN')).length;
    const sessionAlerts = auditEvents.filter((e) => {
      const type = String(e.event_type || '');
      return type.includes('SESSION_EXPIRED') || type.includes('SESSION_INVALIDATED');
    }).length;
    return { failCount, mfaFail, loginFail, sessionAlerts };
  }, [data?.auditFailed24h]);

  const supportSummary = data?.supportStats || null;
  const flags = useMemo<FlagItem[]>(() => {
    const raw = data?.settings as any;
    if (Array.isArray(raw)) return raw as FlagItem[];
    if (Array.isArray(raw?.data)) return raw.data as FlagItem[];
    return [];
  }, [data?.settings]);

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.header}>
          <div className={styles.titleSkeleton} />
        </div>
        <div className={styles.statsGrid}>
          {Array.from({ length: 4 }).map((_, idx) => (
            <div key={idx} className={`${styles.statCard} ${styles.skeletonCard}`}>
              <div className={styles.statHeader}>
                <div className={styles.labelSkeleton} />
                <div className={styles.iconSkeleton} />
              </div>
              <div className={styles.statMain}>
                <div className={styles.valueSkeleton} />
                <div className={styles.badgeSkeleton} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>{t('admin.dashboard_title')}</h1>
      </header>

      <section className={styles.statsGrid} aria-label={t('admin.dashboard_title')}>
        {stats.map(stat => {
          const Icon = stat.icon;
          const hasChange = Boolean(stat.change && stat.change.trim());

          return (
            <article key={stat.id} className={styles.statCard}>
              <div className={styles.statHeader}>
                <div className={styles.labelWrap}>
                  <span className={styles.statLabel}>{stat.label}</span>
                </div>
                <div className={styles.iconBox} aria-hidden="true">
                  <Icon />
                </div>
              </div>

              <div className={styles.statMain}>
                <div className={styles.valueWrap}>
                  <div className={styles.statValue}>{stat.value}</div>
                </div>
                {hasChange ? (
                  <span className={`${styles.statBadge} ${styles[stat.trend]}`}>{stat.change}</span>
                ) : null}
              </div>
            </article>
          );
        })}
      </section>

      <section className={styles.sectionGrid}>
        <article className={styles.sectionCard}>
          <div className={styles.sectionCardHeader}>
            <h2 className={styles.sectionCardTitle}>Operación financiera</h2>
          </div>
          <div className={styles.inlineStats}>
            <div className={styles.inlineStat}><FiDollarSign /> <span>Monto invertido</span><strong>{formatCurrency(financialSummary.totalInvested)}</strong></div>
            <div className={styles.inlineStat}><FiActivity /> <span>Proy. activos</span><strong>{financialSummary.activeProjects}</strong></div>
            <div className={styles.inlineStat}><FiFileText /> <span>Proy. finalizados</span><strong>{financialSummary.closedProjects}</strong></div>
          </div>
          <div className={styles.queueList}>
            <Link to="/admin/retiros" className={styles.queueItem}><span>Retiros pendientes</span><strong>{financialSummary.withdrawalsByStatus.PENDING || 0}</strong></Link>
            <Link to="/admin/retiros" className={styles.queueItem}><span>Retiros aprobados</span><strong>{financialSummary.withdrawalsByStatus.APPROVED || 0}</strong></Link>
            <Link to="/admin/retiros" className={styles.queueItem}><span>Retiros ejecutados</span><strong>{financialSummary.withdrawalsByStatus.EXECUTED || 0}</strong></Link>
            <Link to="/admin/comprobantes-transferencia" className={styles.queueItem}><span>Comprobantes por revisar</span><strong>{queueSummary.transferProofs}</strong></Link>
          </div>
          <div className={styles.footerMetric}>Monto pendiente de ejecutar (retiros): <strong>{formatCurrency(financialSummary.withdrawalsByStatus.pendingAmount || 0)}</strong></div>
        </article>

        <article className={styles.sectionCard}>
          <div className={styles.sectionCardHeader}>
            <h2 className={styles.sectionCardTitle}>Bandeja operativa</h2>
          </div>
          <div className={styles.queueList}>
            <Link to="/admin/aprobacion-usuarios" className={styles.queueItem}><span>Usuarios pendientes</span><strong>{queueSummary.pendingUsers}</strong></Link>
            <Link to="/admin/solicitudes-desarrollador" className={styles.queueItem}><span>Solicitudes de desarrollador</span><strong>{queueSummary.pendingDevReqs}</strong></Link>
            <Link to="/admin/proyectos/documentos" className={styles.queueItem}><span>Revisión documental</span><strong>{queueSummary.documentationReview}</strong></Link>
            <Link to="/admin/proyectos" className={styles.queueItem}><span>Proyectos</span><strong>{Array.isArray(data?.investmentsProjects) ? data!.investmentsProjects.length : 0}</strong></Link>
          </div>
        </article>
      </section>

      <section className={styles.sectionGrid}>
        <article className={styles.sectionCard}>
          <div className={styles.sectionCardHeader}>
            <h2 className={styles.sectionCardTitle}>Riesgo / auditoría / cumplimiento</h2>
          </div>
          <div className={styles.statusRows}>
            <div className={styles.statusRow}><span>Eventos FAIL (24h)</span><strong>{riskSummary.failCount}</strong></div>
            <div className={styles.statusRow}><span>Fallos MFA</span><strong>{riskSummary.mfaFail}</strong></div>
            <div className={styles.statusRow}><span>Fallos login</span><strong>{riskSummary.loginFail}</strong></div>
            <div className={styles.statusRow}><span>Sesiones exp./invalid.</span><strong>{riskSummary.sessionAlerts}</strong></div>
          </div>
          <Link to="/admin/bitacora" className={styles.textLink}>Ir a bitácora de eventos</Link>
        </article>

        <article className={styles.sectionCard}>
          <div className={styles.sectionCardHeader}>
            <h2 className={styles.sectionCardTitle}>Soporte y atención</h2>
          </div>
          <div className={styles.inlineStats}>
            <div className={styles.inlineStat}><FiLifeBuoy /> <span>Abiertos</span><strong>{supportSummary?.open ?? 0}</strong></div>
            <div className={styles.inlineStat}><FiLifeBuoy /> <span>En progreso</span><strong>{supportSummary?.inProgress ?? 0}</strong></div>
            <div className={styles.inlineStat}><FiLifeBuoy /> <span>Resueltos</span><strong>{supportSummary?.resolved ?? 0}</strong></div>
          </div>
          <Link to="/admin/soporte" className={styles.textLink}>Ir a soporte</Link>
        </article>
      </section>

      <section className={styles.sectionGrid}>
        <article className={styles.sectionCard}>
          <div className={styles.sectionCardHeader}>
            <h2 className={styles.sectionCardTitle}>Estado del sistema</h2>
          </div>
          <div className={styles.miniList}>
            {(flags || []).map((flag) => (
              <div key={flag.key} className={styles.statusRow}>
                <span>{flag.key}</span>
                <strong className={flag.value ? styles.badgeOk : styles.badgeMuted}>{flag.value ? 'Activo' : 'Inactivo'}</strong>
              </div>
            ))}
            {(!flags || flags.length === 0) && <div className={styles.emptyLine}>Sin configuración disponible</div>}
          </div>
          <div className={styles.actionRow}>
            <Link to="/admin/configuracion" className={styles.actionPill}><FiSettings /> Configuración</Link>
            <Link to="/admin/roles" className={styles.actionPill}><FiUsers /> Roles</Link>
          </div>
        </article>
      </section>
    </div>
  );
}
